import useAuthStore from '@/stores/auth'
import { portalApi } from '@/util/apiClient'
import { queryClient } from '@/util/queryClient'
import Toasts from '@/util/toasts'
import { useQuery } from '@tanstack/vue-query'
import { computed, readonly, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

const pollingMS = 60000

/**
 * Whether the signed-in account is known to be disabled. Module state rather than
 * store state because it answers one question for one page: the router reads it
 * to keep everyone else off `/account-disabled`, which otherwise signs out
 * whoever opens it.
 */
const disabled = ref(false)

export const accountIsDisabled = readonly(disabled)

/**
 * Watches for the signed-in user's own account having been acted on since this
 * tab loaded — disabled, or its password reset — and ends the session the API
 * has already taken.
 *
 * Needed because neither is immediate: the API validates tokens without checking
 * account state or sessions, so the token in hand outlives the session it came
 * from.
 */
const useAccountEnabled = () => {
  const authStore = useAuthStore()
  const router = useRouter()

  // The two ids the access token gives for the caller, as `callerIds` accepts
  // them.
  const userId = computed(
    () => authStore.claims?.sub ?? authStore.claims?.username,
  )

  const { data: account } = useQuery(
    {
      queryKey: computed(() => ['accountEnabled', userId.value]),
      queryFn: () => portalApi.usersIdGet({ path: { id: userId.value } }),
      enabled: computed(() => authStore.isLoggedIn && !!userId.value),
      refetchInterval: pollingMS,
      retry: false,
    },
    queryClient,
  )

  watch(account, (user) => {
    if (!user) return
    if (user.enabled === false) {
      disabled.value = true
      router.replace({ name: 'account-disabled' })
      return
    }
    /*
      Clear the user session for someone who was issued a temporary password, and
      show a toast with the reason.
    */
    if (user.must_change_password === true) {
      authStore.clearSession()
      Toasts.Info(
        'Password reset',
        'Sign in again with the temporary password you were given.',
      )
    }
  })
}

export default useAccountEnabled
